import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  MapPin,
  Briefcase,
  Clock,
  Heart,
  Zap,
  Users,
  Globe,
  Coffee,
  Laptop,
  DollarSign,
  Plane,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const CareersPage = () => {
  const benefits = [
    { icon: Laptop, title: 'Remote First', description: 'Work from anywhere in the world' },
    { icon: DollarSign, title: 'Competitive Pay', description: 'Top-tier salary and equity' },
    { icon: Heart, title: 'Health Benefits', description: 'Medical, dental, and vision coverage' },
    { icon: Plane, title: 'Unlimited PTO', description: 'Take the time you need' },
    { icon: Coffee, title: 'Learning Budget', description: '$2,000/year for courses and conferences' },
    { icon: Zap, title: 'Latest Tech', description: 'MacBook Pro and home office setup' }
  ];

  const openings = [
    {
      title: 'Senior Frontend Engineer',
      department: 'Engineering',
      location: 'Remote (US/EU)',
      type: 'Full-time',
      description: 'Build beautiful, performant UIs for our social media management platform.',
      requirements: ['5+ years React experience', 'TypeScript proficiency', 'Design system experience']
    },
    {
      title: 'Backend Engineer',
      department: 'Engineering',
      location: 'Remote (Worldwide)',
      type: 'Full-time',
      description: 'Design and build scalable APIs that power our platform.',
      requirements: ['3+ years Python/FastAPI', 'MongoDB experience', 'API design skills']
    },
    {
      title: 'Product Designer',
      department: 'Design',
      location: 'Remote (US/EU)',
      type: 'Full-time',
      description: 'Shape the future of social media management through thoughtful design.',
      requirements: ['4+ years product design', 'Figma expertise', 'SaaS experience preferred']
    },
    {
      title: 'Content Marketing Manager',
      department: 'Marketing',
      location: 'Remote (Worldwide)',
      type: 'Full-time',
      description: 'Create compelling content that educates and inspires our audience.',
      requirements: ['3+ years content marketing', 'SEO knowledge', 'Social media expertise']
    },
    {
      title: 'Customer Success Manager',
      department: 'Customer Success',
      location: 'Remote (Americas)',
      type: 'Full-time',
      description: 'Help our customers achieve their social media goals.',
      requirements: ['2+ years CS experience', 'SaaS background', 'Excellent communication']
    },
    {
      title: 'DevOps Engineer',
      department: 'Engineering',
      location: 'Remote (Worldwide)',
      type: 'Full-time',
      description: 'Build and maintain our cloud infrastructure for reliability and scale.',
      requirements: ['3+ years DevOps', 'AWS/GCP experience', 'Kubernetes knowledge']
    }
  ];

  const values = [
    'Transparent and honest communication',
    'Continuous learning and growth',
    'Work-life balance and flexibility',
    'Diverse and inclusive environment',
    'Impact-driven decision making'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/stellisocial-icon.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-xl text-slate-900" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-500">Social</span></span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/features" className="text-slate-600 hover:text-slate-900">Features</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900">Pricing</Link>
              <Link to="/about" className="text-slate-600 hover:text-slate-900">About</Link>
              <Link to="/careers" className="text-emerald-600 font-medium">Careers</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600">Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-emerald-500 to-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/20 text-white mb-4">We're Hiring!</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Join Us in Shaping the Future of
            <span className="block">Social Media Management</span>
          </h1>
          <p className="text-xl text-emerald-100 max-w-3xl mx-auto mb-8">
            Help us build tools that empower millions of businesses to connect with their audiences.
          </p>
          <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50">
            View Open Positions <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Why Work at StelliSocial?</h2>
            <p className="text-slate-600">We invest in our team's success and wellbeing</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="border-slate-100 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <benefit.icon className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">{benefit.title}</h3>
                        <p className="text-slate-600 text-sm">{benefit.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Culture */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Our Culture</h2>
              <p className="text-slate-600 mb-6">
                We're a remote-first company that values autonomy, trust, and results over hours worked. 
                We believe in hiring smart people and giving them the freedom to do their best work.
              </p>
              <ul className="space-y-3">
                {values.map((value, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-slate-700">{value}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="h-40 bg-emerald-100 rounded-xl"></div>
                <div className="h-32 bg-blue-100 rounded-xl"></div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="h-32 bg-purple-100 rounded-xl"></div>
                <div className="h-40 bg-amber-100 rounded-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Open Positions</h2>
            <p className="text-slate-600">Find your next opportunity at StelliSocial</p>
          </div>
          <div className="space-y-4">
            {openings.map((job, i) => (
              <motion.div
                key={job.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <Card className="border-slate-100 hover:border-emerald-200 hover:shadow-md transition-all">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-slate-900">{job.title}</h3>
                          <Badge variant="secondary">{job.department}</Badge>
                        </div>
                        <p className="text-slate-600 mb-3">{job.description}</p>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" /> {job.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" /> {job.type}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="hidden md:flex flex-wrap gap-2 max-w-xs">
                          {job.requirements.slice(0, 2).map(req => (
                            <Badge key={req} variant="outline" className="text-xs">{req}</Badge>
                          ))}
                        </div>
                        <Button className="bg-emerald-500 hover:bg-emerald-600 whitespace-nowrap">
                          Apply Now
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Don't See a Perfect Fit?</h2>
          <p className="text-emerald-100 mb-8">
            We're always looking for talented people. Send us your resume and we'll keep you in mind for future opportunities.
          </p>
          <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50">
            Submit General Application
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/stellisocial-icon-square-transparent.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
                <span className="font-bold  text-white" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-400">Social</span></span>
              </div>
              <p className="text-sm">The complete social media management platform for teams.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                <li><Link to="/integrations" className="hover:text-white">Integrations</Link></li>
                <li><Link to="/developers" className="hover:text-white">API</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><Link to="/community" className="hover:text-white">Community</Link></li>
                <li><Link to="/webinars" className="hover:text-white">Webinars</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-sm text-center">
            &copy; {new Date().getFullYear()} StelliSocial. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CareersPage;
